/**
 * Раздел «Банковские выписки» (`/bank-statements`, карточка BL-2026-08-15-2024).
 *
 * Flow: перечень разборщиков читается один раз → форма загрузки и
 *       справочник счетов сверху → журнал загрузок → карточка выбранной
 *       загрузки → операции выбранного счёта. Успешная загрузка обновляет
 *       журнал и справочник сразу и открывает свою карточку.
 *
 * Зависимости:
 *   - react: состояние раздела (выбранная загрузка и счёт);
 *   - api.js: loadBankParsers;
 *   - ./BankStatementCommon: rowKey — ключ панели счёта с запасным значением;
 *   - блоки раздела: BankStatementUploadPanel, BankStatementsTable,
 *     BankStatementCard, BankAccountsPanel, BankTransactionsPanel.
 *
 * Связанные файлы:
 *   - src/App.jsx — реестр SECTION_VIEWS (код раздела `bank_statements`);
 *     выдачу раздела ролям решает сервер (section_access);
 *   - docs/specs/BL-2026-08-15-2024-bank-statements.md — §7.1.
 *
 * Выбранная загрузка в адрес не пишется: маршрутизатор приложения знает
 * только пути разделов (router.js), а параметр в адресе потребовал бы
 * разбора строки запроса во всех разделах ради одного.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { loadBankParsers } from "./api";
import { rowKey } from "./BankStatementCommon";
import BankAccountsPanel from "./BankAccountsPanel";
import BankStatementCard from "./BankStatementCard";
import BankStatementsTable from "./BankStatementsTable";
import BankStatementUploadPanel from "./BankStatementUploadPanel";
import BankTransactionsPanel from "./BankTransactionsPanel";

/**
 * Раздел «Банковские выписки».
 *
 * @param {object} user — текущий пользователь; роль решает, активна ли
 *        загрузка и доступна ли разметка принадлежности
 */
export default function BankStatementsSection({ user }) {
  const [parsers, setParsers] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openId, setOpenId] = useState(null);
  const [account, setAccount] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    // Перечень не обязателен: без него остаётся автоопределение банка.
    // Отказ пишется в журнал браузера, форма при этом работает.
    loadBankParsers()
      .then((d) => setParsers(d.parsers || []))
      .catch((e) => console.error("перечень разборщиков не загрузился", e));
  }, []);

  const open = useCallback((id) => {
    setOpenId(id);
    // Карточка стоит под журналом — без прокрутки щелчок по строке выглядел
    // бы так, будто ничего не произошло.
    window.setTimeout(() => {
      const el = cardRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    }, 0);
  }, []);

  const onUploaded = useCallback(
    (res) => {
      setRefreshKey((k) => k + 1);
      if (res.status === 201 && res.data.upload) open(res.data.upload.id);
    },
    [open],
  );

  return (
    <>
      <div className="columns-2">
        <BankStatementUploadPanel
          user={user}
          parsers={parsers}
          onUploaded={onUploaded}
          onOpen={open}
        />
        <BankAccountsPanel
          user={user}
          refreshKey={refreshKey}
          selectedId={account ? account.account_id : null}
          onSelect={setAccount}
        />
      </div>
      {account && (
        <BankTransactionsPanel
          key={rowKey(account.account_id, "acc", account.bank, account.account_number, account.currency)}
          account={account}
          onClose={() => setAccount(null)}
        />
      )}
      <BankStatementsTable
        refreshKey={refreshKey}
        parsers={parsers}
        selectedId={openId}
        onOpen={open}
      />
      <div ref={cardRef}>
        {openId !== null && (
          <BankStatementCard
            key={openId}
            id={openId}
            onOpen={open}
            onClose={() => setOpenId(null)}
          />
        )}
      </div>
    </>
  );
}
